import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Modal,
  Drawer,
  Form,
  InputNumber,
  Typography,
  Row,
  Col,
  message,
  Tooltip,
  Tag,
  List,
  Image,
  Alert,
  Empty,
  Tabs,
  Dropdown,
  Progress,
} from "antd";
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  TeamOutlined,
  PlusOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import axios from "../../utils/axios";
import { getUploadURL, getFileURL } from "../../utils/fileUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { responsiveFilterSelectProps } from "../../utils/responsiveFilterSelect";
import { formatMoney } from "../../utils/formatMoney";
import { formatDateTime, fromNow } from "../../utils/dateFormat";
import { showApiError } from "../../utils/apiError";
import { orderStatusLabel } from "../../constants/orderDisplay";
import {
  PageHeader,
  StatCard,
  StatGrid,
  FilterBar,
  TableCard,
  Section,
  StatusTag,
  statusRenderer,
  REVIEW_STATUS,
  MODAL_WIDTH,
  DRAWER_WIDTH,
  formModalProps,
  tableDefaults,
  paginationDefaults,
} from "../../components/admin";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;
const { TextArea } = Input;

// Country code to country name mapping for consistent display
const countryCodeToName = {
  US: "United States",
  CA: "Canada",
  GB: "United Kingdom",
  AU: "Australia",
  DE: "Germany",
  FR: "France",
  IT: "Italy",
  ES: "Spain",
  NL: "Netherlands",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  JP: "Japan",
  KR: "South Korea",
  CN: "China",
  SG: "Singapore",
  HK: "Hong Kong",
  TW: "Taiwan",
  IN: "India",
  ID: "Indonesia",
  TH: "Thailand",
  MY: "Malaysia",
  PH: "Philippines",
  VN: "Vietnam",
  BR: "Brazil",
  MX: "Mexico",
  AR: "Argentina",
  CL: "Chile",
  CO: "Colombia",
  PE: "Peru",
  ZA: "South Africa",
  EG: "Egypt",
  NG: "Nigeria",
  KE: "Kenya",
  MA: "Morocco",
  AE: "United Arab Emirates",
  SA: "Saudi Arabia",
  IL: "Israel",
  TR: "Turkey",
  RU: "Russia",
  UA: "Ukraine",
  PL: "Poland",
  CZ: "Czech Republic",
  HU: "Hungary",
  RO: "Romania",
  GR: "Greece",
  PT: "Portugal",
  IE: "Ireland",
  BE: "Belgium",
  CH: "Switzerland",
  AT: "Austria",
  LU: "Luxembourg",
  IS: "Iceland",
  NZ: "New Zealand",
};

// Currency to symbol mapping (used by the amount inputs' formatters)
const currencySymbols = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CAD: "C$",
  AUD: "A$",
  CHF: "CHF",
  CNY: "¥",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  KRW: "₩",
  SGD: "S$",
  HKD: "HK$",
  INR: "₹",
  BRL: "R$",
  MXN: "$",
  ARS: "$",
  CLP: "$",
  COP: "$",
  PEN: "S/",
  ZAR: "R",
  EGP: "ج.م",
  NGN: "₦",
  KES: "KSh",
  MAD: "DH",
  AED: "د.إ",
  SAR: "﷼",
  ILS: "₪",
  TRY: "₺",
  RUB: "₽",
  UAH: "₴",
  PLN: "zł",
  CZK: "Kč",
  HUF: "Ft",
  RON: "lei",
  THB: "฿",
  MYR: "RM",
  PHP: "₱",
  VND: "₫",
  NZD: "NZ$",
};

// Get currency symbol for a client
const getCurrencySymbol = (currency) => {
  return currencySymbols[currency?.toUpperCase()] || currency || "$";
};

// Function to get country display name
const getCountryDisplayName = (countryValue) => {
  if (!countryValue) return "";

  // If it's already a full country name (length > 3), return as is
  if (countryValue.length > 3) {
    return countryValue;
  }

  // If it's a country code, convert to full name
  return countryCodeToName[countryValue.toUpperCase()] || countryValue;
};

/** Internal platform vouchers use providerName `vouchain` (backend voucher model default). */
const INTERNAL_VOUCHER_PROVIDER_KEY = "vouchain";
/** Manually uploaded WOGI catalogue (internal, fixed-date vouchers). */
const WOGI_VOUCHER_PROVIDER_KEY = "wogi";

const formatVoucherProviderLabel = (providerName) => {
  const key = String(providerName || "").trim();
  if (key.toLowerCase() === INTERNAL_VOUCHER_PROVIDER_KEY)
    return "Internal vouchers";
  if (key.toLowerCase() === WOGI_VOUCHER_PROVIDER_KEY) return "WOGI";
  return key;
};

const CLIENT_STATUS = {
  ...REVIEW_STATUS,
  pending: { label: "Pending review", tone: "warning" },
};
const renderClientStatus = statusRenderer(CLIENT_STATUS);
const renderReviewStatus = statusRenderer(REVIEW_STATUS);

const STATUS_TABS = [
  { key: "all", statKey: "total", label: "All clients", tone: "default", icon: <TeamOutlined /> },
  { key: "pending", statKey: "pending", label: "Pending review", tone: "warning", icon: <ClockCircleOutlined /> },
  { key: "approved", statKey: "approved", label: "Approved", tone: "success", icon: <CheckCircleOutlined /> },
  { key: "suspended", statKey: "suspended", label: "Suspended", tone: "default", icon: <StopOutlined /> },
  { key: "rejected", statKey: "rejected", label: "Rejected", tone: "danger", icon: <CloseCircleOutlined /> },
];

const humanize = (value) =>
  value
    ? String(value)
        .replace(/_/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase())
    : "-";

const ORDER_STATUS_TONE = {
  completed: "success",
  failed: "danger",
  cancelled: "default",
};

const renderOrderStatus = (status) => {
  const value = status || "pending";
  return (
    <StatusTag tone={ORDER_STATUS_TONE[value] || "info"}>
      {humanize(orderStatusLabel(value))}
    </StatusTag>
  );
};

const personName = (person) =>
  person && typeof person === "object"
    ? [person.firstName, person.lastName].filter(Boolean).join(" ")
    : "";

/** Wallet figures for a client: top-up balance plus whatever credit is unused. */
const fundsOf = (client) => {
  const topUpBalance = client?.topUpBalance || 0;
  const creditUsed = client?.creditUsed || 0;
  const creditLimit = client?.creditLimit || 0;
  const availableCredit = Math.max(0, creditLimit - creditUsed);
  return {
    topUpBalance,
    creditUsed,
    creditLimit,
    availableCredit,
    totalAvailable: topUpBalance + availableCredit,
  };
};

// Action modals can be opened from a table row or on top of the client
// drawer. Both are z-index 1000 by default, so whichever portal was mounted
// last would win; keep the modals above the drawer.
const OVER_DRAWER_Z_INDEX = 1010;

const ClientManagement = () => {
  /** List row is flat `{ _id, ... }`; detail API returns `{ client: { _id, ... }, users }`. */
  const getSelectedClientId = (record) =>
    record?.client?._id ?? record?._id;

  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [pricingTierOptions, setPricingTierOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [customTierModalVisible, setCustomTierModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    tier: undefined,
    search: "",
    dateRange: null,
  });
  const [activeStatusFilter, setActiveStatusFilter] = useState("all"); // Track which status panel is active
  // Either the detail payload `{ client, users, ... }` while the drawer is
  // open, or a flat list row when an action is started from the table.
  const [selectedClient, setSelectedClient] = useState(null);
  // Tracks whether the selected client's business-license file is actually
  // reachable on the server. Old clients still have a `businessLicenseFile`
  // path in the DB but the file itself may have been wiped (e.g. by a
  // container redeploy before persistent storage was configured).
  // 'unknown' = not probed yet, 'available' = HEAD 2xx, 'missing' = HEAD 404.
  const [bizLicenseStatus, setBizLicenseStatus] = useState("unknown");
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [suspendModalVisible, setSuspendModalVisible] = useState(false);
  const [reactivateModalVisible, setReactivateModalVisible] = useState(false);
  // Client details drawer
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailTab, setDetailTab] = useState("overview");
  const [detailLoading, setDetailLoading] = useState(false);
  const [redeemMetricsLoading, setRedeemMetricsLoading] = useState(false);
  const [redeemMetrics, setRedeemMetrics] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [creditModalVisible, setCreditModalVisible] = useState(false);
  const [topUpModalVisible, setTopUpModalVisible] = useState(false);
  const [topUpRequestsModalVisible, setTopUpRequestsModalVisible] =
    useState(false);
  const [topUpRequests, setTopUpRequests] = useState([]);
  const [topUpActionLoading, setTopUpActionLoading] = useState(false);
  const [topUpActionRequestId, setTopUpActionRequestId] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({}); // Exchange rate state
  const [loadingRates, setLoadingRates] = useState(false); // Exchange rate loading state
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    suspended: 0,
  });

  const [approveForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [suspendForm] = Form.useForm();
  const [topUpForm] = Form.useForm();
  const [creditForm] = Form.useForm(); // Credit settings form
  const [editForm] = Form.useForm(); // Edit client form

  // Fetch real-time exchange rate
  const fetchExchangeRate = async (fromCurrency, toCurrency = "USD") => {
    if (fromCurrency === toCurrency) return 1;

    try {
      setLoadingRates(true);
      const response = await axios.get(
        `/currency/exchange-rate?from=${fromCurrency}&to=${toCurrency}`
      );
      // API shape can vary depending on the upstream FX provider. Be
      // explicit about validating the rate is a finite number before
      // letting it land in state — pricing & top-up math downstream
      // multiplies by it and will silently produce NaN otherwise.
      const rawRate = Number(response?.data?.rate ?? response?.data?.data?.rate);
      if (!Number.isFinite(rawRate) || rawRate <= 0) {
        console.error('Exchange rate response missing/invalid', response?.data);
        return null;
      }
      setExchangeRates((prev) => ({
        ...prev,
        [`${fromCurrency}_${toCurrency}`]: rawRate,
      }));
      return rawRate;
    } catch (error) {
      console.error("Failed to fetch exchange rate:", error);
      return null;
    } finally {
      setLoadingRates(false);
    }
  };

  // Format USD equivalent display
  const formatUSDEquivalent = (amount, currency) => {
    if (!amount || currency === "USD") return null;

    const rateKey = `${currency}_USD`;
    const rate = exchangeRates[rateKey];

    if (rate) {
      const usdAmount = (amount * rate).toFixed(2);
      return `≈ ${formatMoney(parseFloat(usdAmount), "USD")}`;
    }

    return "Loading USD equivalent...";
  };

  // Fetch statistics separately to ensure accuracy.
  // NOTE: Do NOT fall back to the in-memory `clients` slice — that is only the
  // current page (e.g. 20 of 1000) and would dramatically misreport totals.
  // If the stats endpoint fails, surface the failure instead of silently
  // showing wrong numbers.
  const fetchStatistics = async () => {
    try {
      const response = await axios.get("/admin/clients/statistics");
      setStatistics(response.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      setStatistics(null);
    }
  };

  // Fetch clients data. Returns the rows so a reviewer can be moved on to the
  // next pending registration after a decision.
  const fetchClients = async (
    page = 1,
    pageSize = 20,
    filtersOverride = null
  ) => {
    setLoading(true);
    try {
      const currentFilters = filtersOverride || filters;
      const params = {
        page,
        limit: pageSize,
      };

      // Only add filter params if they have values
      if (currentFilters.status) {
        params.status = currentFilters.status;
      }
      if (currentFilters.tier) {
        params.tier = currentFilters.tier;
      }
      if (currentFilters.search) {
        params.search = currentFilters.search;
      }

      // Handle date range
      if (currentFilters.dateRange && currentFilters.dateRange.length === 2) {
        params.startDate = currentFilters.dateRange[0].format("YYYY-MM-DD");
        params.endDate = currentFilters.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await axios.get("/admin/clients", { params });
      const rows = response.data.clients || [];
      setClients(rows);
      const pg = response.data.pagination || {};
      setPagination({
        current: page,
        pageSize: pageSize,
        total: pg.totalCount ?? pg.total ?? pg.totalItems ?? 0,
      });

      // Fetch statistics separately for accuracy
      fetchStatistics();
      return rows;
    } catch (error) {
      console.error("Error fetching clients:", error);
      showApiError(error, "Failed to load clients");
    } finally {
      setLoading(false);
    }
    return [];
  };

  // Fetch pricing tier options
  const fetchPricingTierOptions = async () => {
    try {
      const response = await axios.get("/admin/pricing-tiers/options");
      const raw = Array.isArray(response.data)
        ? response.data
        : response.data?.items ?? response.data?.options ?? [];
      const data = raw.map((item) => ({
        value: item.name,
        label: item.displayName,
      }));
      setPricingTierOptions(data);
    } catch (error) {
      console.error("Error fetching pricing tier options:", error);
      showApiError(error, "Failed to load pricing tier options");
    }
  };

  // Fetch all distinct voucher providers (used for the per-client
  // "allowed providers" whitelist in the Edit Client modal).
  const fetchProviderOptions = async () => {
    try {
      const response = await axios.get("/admin/vouchers/filter-options");
      const list = Array.isArray(response.data?.providers)
        ? response.data.providers
        : [];
      setProviderOptions(list);
    } catch (error) {
      console.error("Error fetching provider options:", error);
    }
  };

  // Handle status panel click
  const handleStatusPanelClick = (status) => {
    setActiveStatusFilter(status);
    const newFilters = {
      ...filters,
      status: status === "all" ? undefined : status,
      // Reset tier filter if switching to pending/rejected status (only approved/suspended can have tiers)
      tier:
        status === "pending" || status === "rejected"
          ? undefined
          : filters.tier,
    };
    setFilters(newFilters);
    fetchClients(1, pagination.pageSize, newFilters);
  };

  // ------------------------------------------------------------ drawer

  const closeDrawer = () => {
    setDetailModalVisible(false);
    setDetailLoading(false);
  };

  // Clear the drawer's data only once it has slid out, so the content does
  // not disappear mid-animation.
  const handleDrawerAfterOpenChange = (open) => {
    if (open) return;
    setSelectedClient(null);
    setRedeemMetrics(null);
    setDetailTab("overview");
  };

  // Handle view client details
  const handleViewDetails = async (clientId) => {
    setDetailModalVisible(true);
    setDetailLoading(true);
    setSelectedClient(null);
    setRedeemMetrics(null);
    setDetailTab("overview");
    setBizLicenseStatus("unknown");
    try {
      const clientResponse = await axios.get(`/admin/clients/${clientId}`);
      setSelectedClient(clientResponse.data);
      fetchClientRedeemMetrics(clientId);
    } catch (error) {
      console.error("Error fetching client details:", error);
      showApiError(error, "Failed to load client details");
      setDetailModalVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // Reload the open drawer in place after an action changed the client.
  const refreshOpenDetails = async (clientId) => {
    if (!clientId) return;
    try {
      const detailRes = await axios.get(`/admin/clients/${clientId}`);
      setSelectedClient(detailRes.data);
    } catch (e) {
      console.error("Error refreshing client details:", e);
    }
  };

  const fetchClientRedeemMetrics = async (clientId) => {
    if (!clientId) return null;
    try {
      setRedeemMetricsLoading(true);
      const response = await axios.get(`/admin/clients/${clientId}/redeem-metrics`);
      setRedeemMetrics(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching client redeem metrics:", error);
      showApiError(error, "Failed to load client redeem metrics");
      return null;
    } finally {
      setRedeemMetricsLoading(false);
    }
  };

  // A modal started from a table row owns `selectedClient`; one started from
  // the drawer shares it with the drawer, which must keep showing the client.
  const releaseSelection = () => {
    if (!detailModalVisible) setSelectedClient(null);
  };

  const afterClientAction = (clientId) => {
    if (detailModalVisible) {
      refreshOpenDetails(clientId);
    } else {
      setSelectedClient(null);
    }
  };

  // After approving or rejecting, open the next pending registration on this
  // page so a reviewer can work through the queue without leaving the drawer.
  const advanceAfterReview = async (reviewed, verb) => {
    const reviewedId = String(reviewed?._id || "");
    const name = reviewed?.companyName || "Client";
    const indexInList = clients.findIndex(
      (row) => String(row._id) === reviewedId
    );
    const rows = await fetchClients(pagination.current, pagination.pageSize);
    fetchStatistics();
    const isNextPending = (row) =>
      row.status === "pending" && String(row._id) !== reviewedId;
    const next =
      rows.slice(Math.max(indexInList, 0)).find(isNextPending) ||
      rows.find(isNextPending);

    if (detailModalVisible && next) {
      message.success(`${name} ${verb}. Showing the next pending registration.`);
      handleViewDetails(next._id);
      return;
    }
    if (detailModalVisible) {
      closeDrawer();
    } else {
      setSelectedClient(null);
    }
    message.success(
      activeStatusFilter === "pending"
        ? `${name} ${verb}. No more pending registrations on this page.`
        : `${name} ${verb}.`
    );
  };

  // ------------------------------------------------------------ actions

  // Handle approve client
  const handleApproveClient = async (values) => {
    const reviewed = selectedClient?.client || selectedClient;
    let approved = false;
    try {
      setActionSubmitting(true);
      await axios.post(
        `/admin/clients/${
          selectedClient.client?._id || selectedClient._id
        }/approve`,
        values
      );
      approved = true;
    } catch (error) {
      console.error("Error approving client:", error);
      showApiError(error, "Failed to approve client");
    } finally {
      setActionSubmitting(false);
    }
    if (!approved) return;
    // Only toggle the modal visibility here. The form reset is deferred to
    // the modal's `afterClose` callback so users don't see the input value
    // flash back to the form's initial value (e.g. 5000) during the closing
    // animation.
    setApproveModalVisible(false);
    await advanceAfterReview(reviewed, "approved");
  };

  // Handle reject client
  const handleRejectClient = async (values) => {
    const reviewed = selectedClient?.client || selectedClient;
    let rejected = false;
    try {
      setActionSubmitting(true);
      await axios.post(
        `/admin/clients/${
          selectedClient.client?._id || selectedClient._id
        }/reject`,
        values
      );
      rejected = true;
    } catch (error) {
      console.error("Error rejecting client:", error);
      showApiError(error, "Failed to reject client");
    } finally {
      setActionSubmitting(false);
    }
    if (!rejected) return;
    setRejectModalVisible(false);
    rejectForm.resetFields();
    await advanceAfterReview(reviewed, "rejected");
  };

  // Handle suspend client
  const handleSuspendClient = async (values) => {
    const target = selectedClient?.client || selectedClient;
    try {
      setActionSubmitting(true);
      await axios.post(
        `/admin/clients/${
          selectedClient.client?._id || selectedClient._id
        }/suspend`,
        values
      );
      message.success(`${target?.companyName || "Client"} suspended`);
      setSuspendModalVisible(false);
      suspendForm.resetFields();
      afterClientAction(target?._id);
      fetchClients(pagination.current, pagination.pageSize);
      fetchStatistics(); // Update statistics after suspension
    } catch (error) {
      console.error("Error suspending client:", error);
      showApiError(error, "Failed to suspend client");
    } finally {
      setActionSubmitting(false);
    }
  };

  // Handle reactivate client
  const handleReactivateClient = async () => {
    const target = selectedClient?.client || selectedClient;
    try {
      setActionSubmitting(true);
      await axios.post(
        `/admin/clients/${
          selectedClient.client?._id || selectedClient._id
        }/reactivate`
      );
      message.success(`${target?.companyName || "Client"} reactivated`);
      setReactivateModalVisible(false);
      afterClientAction(target?._id);
      fetchClients(pagination.current, pagination.pageSize);
      fetchStatistics(); // Update statistics after reactivation
    } catch (error) {
      console.error("Error reactivating client:", error);
      showApiError(error, "Failed to reactivate client");
    } finally {
      setActionSubmitting(false);
    }
  };

  // Handle edit client
  const handleEditClient = async (values) => {
    try {
      const clientId = getSelectedClientId(selectedClient);
      if (!clientId) {
        message.error("Missing client id");
        return;
      }
      setActionSubmitting(true);
      const { data } = await axios.put(`/admin/clients/${clientId}`, {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        companyName: values.companyName,
        name: values.name,
        address: values.address,
        country: values.country,
        tier: values.tier,
        creditLimit: values.creditLimit,
        notes: values.notes,
        // Empty array → no restriction (client can see all providers).
        allowedProviders: Array.isArray(values.allowedProviders)
          ? values.allowedProviders
          : [],
      });
      message.success(`${values.companyName || "Client"} updated`);
      const updated = data?.client;
      if (updated?._id) {
        setClients((prev) =>
          prev.map((row) =>
            String(row._id) === String(updated._id)
              ? { ...row, ...updated }
              : row
          )
        );
      }
      setEditModalVisible(false);
      editForm.resetFields();
      afterClientAction(clientId);
      await fetchClients(pagination.current, pagination.pageSize);
      fetchStatistics();
    } catch (error) {
      console.error("Error updating client:", error);
      showApiError(error, "Failed to update client");
    } finally {
      setActionSubmitting(false);
    }
  };

  const closeRejectModal = () => {
    setRejectModalVisible(false);
    rejectForm.resetFields();
    releaseSelection();
  };

  const closeSuspendModal = () => {
    setSuspendModalVisible(false);
    suspendForm.resetFields();
    releaseSelection();
  };

  const closeReactivateModal = () => {
    setReactivateModalVisible(false);
    releaseSelection();
  };

  const closeEditModal = () => {
    setEditModalVisible(false);
    editForm.resetFields();
    releaseSelection();
  };

  // Start an action from a table row (the drawer is closed).
  const openRowAction = (record, action) => {
    setSelectedClient(record);
    if (action === "edit") setEditModalVisible(true);
    if (action === "suspend") setSuspendModalVisible(true);
    if (action === "reactivate") setReactivateModalVisible(true);
  };

  // Pick up navigation state from Admin Dashboard cards (e.g.
  // navigate('/admin/clients', { state: { filterStatus: 'pending' } })).
  // Apply once and clear the state so a manual reset doesn't get re-applied.
  useEffect(() => {
    const state = location.state;
    if (!state) return;
    if (state.filterStatus) {
      handleStatusPanelClick(state.filterStatus);
    }
    if (state.filterOverdue) {
      // Map "overdue clients" deeplink onto the approved-status panel so the
      // user lands on the most relevant subset rather than the unfiltered
      // table. A first-class overdue filter can be added later.
      handleStatusPanelClick("approved");
    }
    if (state.clientId) {
      handleViewDetails(state.clientId).catch(() => {});
    }
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname]);

  // Probe the business-license file with HEAD whenever the selected client
  // changes, so we can show a clear "file missing, please ask the client to
  // re-upload" message instead of opening a blank/JSON page when the user
  // clicks Open or Download.
  useEffect(() => {
    const filePath = selectedClient?.client?.businessLicenseFile;
    if (!filePath) {
      setBizLicenseStatus("unknown");
      return;
    }

    let cancelled = false;
    setBizLicenseStatus("unknown");
    (async () => {
      try {
        const res = await fetch(getFileURL(filePath), { method: "HEAD" });
        if (cancelled) return;
        if (res.ok) {
          setBizLicenseStatus("available");
        } else if (res.status === 404) {
          setBizLicenseStatus("missing");
        } else {
          // Network/CORS/other errors: don't block the user — fall back to
          // the normal UI and let the browser surface any real failure.
          setBizLicenseStatus("unknown");
        }
      } catch {
        if (!cancelled) setBizLicenseStatus("unknown");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedClient]);

  // Handle credit check (currently not used but kept for future implementation)
  /*
  const handleCreditCheck = async (clientId) => {
    try {
      const response = await axios.post(`/admin/credit/check/${clientId}`);
      message.success('Credit check completed');

      // Update client data with latest info
      await handleViewDetails(clientId);

      return response.data.result;
    } catch (error) {
      console.error('Error running credit check:', error);
      message.error(error.response?.data?.message || 'Credit check failed');
      return null;
    }
  };
  */

  // Handle reset credit violations (currently not used but kept for future implementation)
  /*
  const handleResetViolations = async (clientId, reason) => {
    try {
      await axios.post(`/admin/credit/reset-violations/${clientId}`, { reason });
      message.success('Credit violations reset successfully');

      // Update client data
      await handleViewDetails(clientId);

      return true;
    } catch (error) {
      console.error('Error resetting violations:', error);
      message.error(error.response?.data?.message || 'Failed to reset credit violations');
      return false;
    }
  };
  */

  // Handle update credit settings
  const handleUpdateCreditSettings = async (clientId, settings) => {
    try {
      const { data } = await axios.put(`/admin/clients/${clientId}`, settings);
      message.success("Credit settings updated successfully");
      const updated = data?.client;
      if (updated?._id) {
        setClients((prev) =>
          prev.map((row) =>
            String(row._id) === String(updated._id)
              ? { ...row, ...updated }
              : row
          )
        );
      }
      if (detailModalVisible) {
        try {
          const detailRes = await axios.get(`/admin/clients/${clientId}`);
          setSelectedClient(detailRes.data);
        } catch (e) {
          console.error("Error refreshing client details after credit update:", e);
        }
      }
      await fetchClients(pagination.current, pagination.pageSize);
      fetchStatistics();
      return true;
    } catch (error) {
      console.error("Error updating credit settings:", error);
      showApiError(error, "Failed to update credit settings");
      return false;
    }
  };

  // Fetch top-up request list
  const fetchTopUpRequests = async (clientId) => {
    try {
      const response = await axios.get(
        `/admin/clients/${clientId}/topup-requests`
      );
      setTopUpRequests(response.data.requests || response.data || []);
    } catch (error) {
      console.error("Error fetching top-up requests:", error);
      showApiError(error, "Failed to load top-up requests");
    }
  };

  // Handle top-up request approval
  const handleTopUpRequestApproval = async (requestId, action, reason = "") => {
    if (topUpActionLoading) return;
    const verb = action === "approve" ? "approved" : "rejected";
    const verbing = action === "approve" ? "approving" : "rejecting";
    try {
      setTopUpActionLoading(true);
      setTopUpActionRequestId(requestId);
      await axios.post(`/admin/topup-requests/${requestId}/${action}`, {
        reviewNotes: reason,
      });
      message.success(`Top-up request ${verb} successfully`);
      fetchTopUpRequests(selectedClient.client?._id || selectedClient._id);
      fetchClients(pagination.current, pagination.pageSize); // Refresh client list to update balance
    } catch (error) {
      console.error(`Error ${verbing} top-up request:`, error);
      showApiError(error, `Failed to ${action} top-up request`);
    } finally {
      setTopUpActionLoading(false);
      setTopUpActionRequestId(null);
    }
  };

  // Manual top-up
  const handleManualTopUp = async (values) => {
    try {
      await axios.post(
        `/admin/clients/${
          selectedClient.client?._id || selectedClient._id
        }/topup`,
        {
          amount: values.amount,
          description: values.description || "Manual top-up by admin",
        }
      );
      message.success("Top-up completed successfully");
      setTopUpModalVisible(false);
      topUpForm.resetFields();
      fetchClients(pagination.current, pagination.pageSize); // Refresh client list
    } catch (error) {
      console.error("Error processing top-up:", error);
      showApiError(error, "Failed to process top-up");
    }
  };

  const tierLabel = (value) => {
    if (!value) return "-";
    const option = pricingTierOptions.find((o) => o.value === value);
    return option?.label ? option.label.replace(/ \(.*\)$/, "") : value;
  };

  const downloadBusinessLicense = (client) => {
    const link = document.createElement("a");
    link.href = getFileURL(client.businessLicenseFile);
    link.download = `${client.companyName}_business_license`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const rowMenuItems = (record) => {
    if (record.status === "approved") {
      return [
        { key: "edit", label: "Edit settings", icon: <EditOutlined /> },
        { type: "divider" },
        { key: "suspend", label: "Suspend", icon: <StopOutlined />, danger: true },
      ];
    }
    if (record.status === "suspended") {
      return [
        { key: "reactivate", label: "Reactivate", icon: <CheckCircleOutlined /> },
      ];
    }
    return [];
  };

  // Table columns configuration
  const columns = [
    {
      title: "Client",
      dataIndex: "companyName",
      key: "companyName",
      render: (text, record) => (
        <div style={{ minWidth: 200 }}>
          <div className="cell-main">{text || record.name || "-"}</div>
          <div className="cell-sub">{record.email}</div>
        </div>
      ),
    },
    {
      title: "Contact",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <div>{name || personName(record) || "-"}</div>
          {record.phone ? <div className="cell-sub">{record.phone}</div> : null}
        </div>
      ),
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      render: (country, record) => (
        <div>
          <div>{getCountryDisplayName(country) || "-"}</div>
          <div className="cell-sub">{record.currency || "-"}</div>
        </div>
      ),
    },
    {
      title: "Tier",
      dataIndex: "tier",
      key: "tier",
      render: (tier, record) =>
        record.status === "approved" || record.status === "suspended" ? (
          tierLabel(tier)
        ) : (
          <span className="muted">-</span>
        ),
    },
    {
      title: (
        <Tooltip title="Top-up balance plus unused credit">Available</Tooltip>
      ),
      key: "accountBalance",
      align: "right",
      width: 170,
      render: (_, record) => (
        <span className="cell-money">
          {formatMoney(fundsOf(record).totalAvailable, record.currency)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: renderClientStatus,
    },
    {
      title: "Registered",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (date) => (
        <div>
          <div>{formatDateTime(date)}</div>
          <div className="cell-sub">{fromNow(date)}</div>
        </div>
      ),
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      align: "right",
      render: (_, record) => {
        const items = rowMenuItems(record);
        const isPending = record.status === "pending";
        return (
          // Stops the row click (which opens the drawer) for the buttons and
          // for the dropdown menu, whose clicks bubble through the portal.
          <span className="table-actions" onClick={(e) => e.stopPropagation()}>
            <Button
              size="small"
              type={isPending ? "primary" : "default"}
              onClick={() => handleViewDetails(record._id)}
            >
              {isPending ? "Review" : "View"}
            </Button>
            {items.length > 0 ? (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items,
                  onClick: ({ key }) => openRowAction(record, key),
                }}
              >
                <Button size="small">
                  More <DownOutlined />
                </Button>
              </Dropdown>
            ) : null}
          </span>
        );
      },
    },
  ];

  // Handle table change
  const handleTableChange = (nextPagination) => {
    fetchClients(nextPagination.current, nextPagination.pageSize);
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Tier and date range apply as soon as they change; search is debounced
  // by the effect below.
  const applyFilter = (key, value) => {
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
    fetchClients(1, pagination.pageSize, nextFilters);
  };

  // Reset filters
  const resetFilters = () => {
    const resetFiltersData = {
      status: undefined,
      tier: undefined,
      search: "",
      dateRange: null,
    };
    setFilters(resetFiltersData);
    setActiveStatusFilter("all"); // Reset to all registrations
    // Immediately fetch clients without filters
    fetchClients(1, pagination.pageSize, resetFiltersData);
  };

  useEffect(() => {
    fetchClients();
    fetchStatistics();
    fetchPricingTierOptions();
    fetchProviderOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (filters.search !== undefined) {
        fetchClients(1, pagination.pageSize);
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Antd <Form initialValues> only applies on first mount. Without this, the
  // Edit Client modal would keep showing the previously edited client's values
  // (companyName / name / phone…) when the admin opens it for a different
  // client, and submitting would overwrite the new client's fields with the
  // old one's data. Re-sync the form whenever selectedClient changes while
  // the edit modal is open.
  useEffect(() => {
    if (editModalVisible && selectedClient) {
      // Opened from a table row → flat client; opened from the drawer → the
      // detail payload `{ client, users, ... }`. Both carry the same client
      // document.
      const source = selectedClient.client || selectedClient;
      editForm.resetFields();
      editForm.setFieldsValue({
        tier: "basic",
        ...source,
        allowedProviders: Array.isArray(source.allowedProviders)
          ? source.allowedProviders
          : [],
      });
    }
  }, [editModalVisible, selectedClient, editForm]);

  // ------------------------------------------------------------ render helpers

  const activeTab =
    STATUS_TABS.find((tab) => tab.key === activeStatusFilter) || STATUS_TABS[0];
  const filtersDirty = Boolean(
    filters.search ||
      filters.tier ||
      (filters.dateRange && filters.dateRange.length) ||
      activeStatusFilter !== "all"
  );
  const showTierFilter =
    activeStatusFilter === "approved" ||
    activeStatusFilter === "suspended" ||
    Boolean(filters.tier);

  // The client an action modal is about (either shape of selectedClient).
  const target = selectedClient?.client || selectedClient || null;
  const targetName = target?.companyName || target?.email || "this client";

  // Drawer data (only when selectedClient is the detail payload).
  const detail = selectedClient?.client ? selectedClient : null;
  const detailClient = detail?.client || null;
  const detailStatus = detailClient?.status;
  const detailHasAccount =
    detailStatus === "approved" || detailStatus === "suspended";
  const detailFunds = fundsOf(detailClient);
  const detailCurrency = detailClient?.currency;
  const detailUsers = detail?.users || [];
  const licensePath = detailClient?.businessLicenseFile;
  const licenseName = licensePath ? licensePath.split("/").pop() : "";
  const licenseIsImage = licensePath
    ? /\.(jpg|jpeg|png|gif)$/.test(licensePath.toLowerCase())
    : false;
  const creditUtilization =
    detailFunds.creditLimit > 0
      ? Math.round((detailFunds.creditUsed / detailFunds.creditLimit) * 100)
      : 0;
  const utilizationColor =
    creditUtilization >= 80
      ? "var(--c-danger)"
      : creditUtilization >= 60
      ? "var(--c-warning)"
      : "var(--c-success)";

  const redeemColumns = [
    {
      title: "Voucher",
      dataIndex: "voucherName",
      key: "voucherName",
      render: (text, record) => (
        <div>
          <div className="cell-main">{text || record.voucherId}</div>
          <div className="cell-sub">{record.brand || record.voucherId}</div>
        </div>
      ),
    },
    {
      title: "Provider",
      dataIndex: "providerName",
      key: "providerName",
      render: (value) => formatVoucherProviderLabel(value) || "-",
    },
    {
      title: "Total",
      dataIndex: "totalRedeemQuantity",
      key: "totalRedeemQuantity",
      align: "right",
    },
    {
      title: "Redeemed",
      dataIndex: "completedRedeemQuantity",
      key: "completedRedeemQuantity",
      align: "right",
    },
    {
      title: "Unredeemed",
      dataIndex: "pendingRedeemQuantity",
      key: "pendingRedeemQuantity",
      align: "right",
    },
    {
      title: "Last redeemed",
      dataIndex: "lastRedeemAt",
      key: "lastRedeemAt",
      render: formatDateTime,
    },
  ];

  const recentOrderColumns = [
    {
      title: "Order",
      key: "order",
      render: (_, order) => (
        <span className="cell-mono">
          {order.orderNumber || `Order ${order._id?.slice(-8)}`}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderOrderStatus,
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      render: (_, order) => (
        <span className="cell-money">
          {formatMoney(order.totalAmount || 0, detailCurrency)}
        </span>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: formatDateTime,
    },
  ];

  const recentTopUpColumns = [
    {
      title: "Request",
      dataIndex: "requestNumber",
      key: "requestNumber",
      render: (value) => <span className="cell-mono">{value || "-"}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => renderReviewStatus(status || "pending"),
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      render: (_, request) => (
        <span className="cell-money">
          {formatMoney(request.amount || 0, request.currency || detailCurrency)}
        </span>
      ),
    },
    {
      title: "Submitted",
      dataIndex: "createdAt",
      key: "createdAt",
      render: formatDateTime,
    },
  ];

  const userColumns = [
    {
      title: "User",
      key: "user",
      render: (_, user) => (
        <div>
          <div className="cell-main">{personName(user) || "-"}</div>
          <div className="cell-sub">{user.email}</div>
        </div>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: humanize,
    },
  ];

  const nestedTableProps = {
    size: "small",
    pagination: false,
    scroll: { x: "max-content" },
  };

  const renderLicenseSummary = () => {
    if (!licensePath) return <span className="muted">Not uploaded</span>;
    if (bizLicenseStatus === "missing") {
      return (
        <Space size={6} wrap>
          <Text type="warning">File missing on the server</Text>
          <Typography.Link onClick={() => setDetailTab("documents")}>
            Details
          </Typography.Link>
        </Space>
      );
    }
    return (
      <Typography.Link onClick={() => setDetailTab("documents")}>
        {licenseName}
      </Typography.Link>
    );
  };

  const renderDetailTabs = () => {
    const overviewTab = (
      <>
        {detailClient.rejectionReason ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              detailClient.rejectedAt
                ? `Registration rejected on ${formatDateTime(detailClient.rejectedAt)}`
                : "Registration rejected"
            }
            description={detailClient.rejectionReason}
          />
        ) : null}
        {detailClient.suspensionReason ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              detailClient.suspendedAt
                ? `Suspended on ${formatDateTime(detailClient.suspendedAt)}`
                : "Account suspended"
            }
            description={detailClient.suspensionReason}
          />
        ) : null}

        {detailHasAccount ? (
          <StatGrid min={180}>
            <StatCard
              label="Available funds"
              value={formatMoney(detailFunds.totalAvailable, detailCurrency)}
              hint="Top-up balance plus unused credit"
            />
            <StatCard
              label="Top-up balance"
              value={formatMoney(detailFunds.topUpBalance, detailCurrency)}
            />
            <StatCard
              label="Credit used"
              value={formatMoney(detailFunds.creditUsed, detailCurrency)}
              hint={
                detailFunds.creditLimit > 0
                  ? `${creditUtilization}% of ${formatMoney(detailFunds.creditLimit, detailCurrency)}`
                  : "No credit limit"
              }
            />
          </StatGrid>
        ) : null}

        <Section>
          <div className="admin-grid-2">
            <div>
              <Section title="Company and contact">
                <dl className="kv">
                  <dt>Company</dt>
                  <dd>{detailClient.companyName || "-"}</dd>
                  <dt>Contact person</dt>
                  <dd>{detailClient.name || "-"}</dd>
                  <dt>First name</dt>
                  <dd>
                    {detailClient.firstName || detailUsers[0]?.firstName || "-"}
                  </dd>
                  <dt>Last name</dt>
                  <dd>
                    {detailClient.lastName || detailUsers[0]?.lastName || "-"}
                  </dd>
                  <dt>Email</dt>
                  <dd>
                    {detailClient.email ? (
                      <Text copyable>{detailClient.email}</Text>
                    ) : (
                      "-"
                    )}
                  </dd>
                  <dt>Phone</dt>
                  <dd>
                    {detailClient.phone || <span className="muted">Not set</span>}
                  </dd>
                  <dt>Address</dt>
                  <dd>
                    {detailClient.address || (
                      <span className="muted">Not set</span>
                    )}
                  </dd>
                  <dt>Country</dt>
                  <dd>{getCountryDisplayName(detailClient.country) || "-"}</dd>
                  <dt>Currency</dt>
                  <dd>{detailClient.currency || "-"}</dd>
                </dl>
              </Section>
            </div>
            <div>
              <Section title="Registration">
                <dl className="kv">
                  <dt>Registered</dt>
                  <dd>
                    {formatDateTime(detailClient.createdAt)}{" "}
                    <span className="muted">
                      ({fromNow(detailClient.createdAt)})
                    </span>
                  </dd>
                  {detailHasAccount && detailClient.approvedAt ? (
                    <>
                      <dt>Approved</dt>
                      <dd>
                        {formatDateTime(detailClient.approvedAt)}
                        {personName(detailClient.approvedBy) ? (
                          <span className="muted">
                            {" "}
                            by {personName(detailClient.approvedBy)}
                          </span>
                        ) : null}
                      </dd>
                    </>
                  ) : null}
                  {detailStatus === "rejected" && detailClient.rejectedAt ? (
                    <>
                      <dt>Rejected</dt>
                      <dd>{formatDateTime(detailClient.rejectedAt)}</dd>
                    </>
                  ) : null}
                  {detailStatus === "suspended" && detailClient.suspendedAt ? (
                    <>
                      <dt>Suspended</dt>
                      <dd>{formatDateTime(detailClient.suspendedAt)}</dd>
                    </>
                  ) : null}
                  {detailClient.registrationIP ? (
                    <>
                      <dt>Registration IP</dt>
                      <dd className="cell-mono">{detailClient.registrationIP}</dd>
                    </>
                  ) : null}
                  <dt>Business license</dt>
                  <dd>{renderLicenseSummary()}</dd>
                </dl>
              </Section>
            </div>
          </div>
        </Section>

        {detailClient.notes ? (
          <Section title="Notes">
            <div style={{ whiteSpace: "pre-wrap" }}>{detailClient.notes}</div>
          </Section>
        ) : null}
      </>
    );

    const documentsTab = (
      <Section
        title="Business license"
        description={licensePath ? licenseName : undefined}
        extra={
          licensePath && bizLicenseStatus !== "missing" ? (
            <Space>
              <Button
                size="small"
                icon={<ExportOutlined />}
                onClick={() => window.open(getFileURL(licensePath), "_blank")}
              >
                Open
              </Button>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => downloadBusinessLicense(detailClient)}
              >
                Download
              </Button>
            </Space>
          ) : null
        }
      >
        {!licensePath ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No business license uploaded"
          />
        ) : bizLicenseStatus === "missing" ? (
          <Alert
            type="warning"
            showIcon
            message="The business license file is missing on the server"
            description={
              <>
                <div>
                  The client record still points to this file, but it is no
                  longer in storage (likely lost in an earlier deployment
                  before persistent storage was configured). Ask the client to
                  upload the business license again.
                </div>
                <div className="cell-sub" style={{ marginTop: 6 }}>
                  Reference path: <code>{licensePath}</code>
                </div>
              </>
            }
          />
        ) : licenseIsImage ? (
          <div
            style={{
              background: "var(--c-bg-subtle)",
              border: "1px solid var(--c-border-soft)",
              borderRadius: 8,
              padding: 8,
              textAlign: "center",
            }}
          >
            <Image
              src={getFileURL(licensePath)}
              alt="Business license"
              style={{ maxWidth: "100%", maxHeight: 460, objectFit: "contain" }}
              // The HEAD probe can be inconclusive (or the file can vanish
              // between probe and render); promote to 'missing' so the
              // warning replaces a broken image.
              onError={() => setBizLicenseStatus("missing")}
            />
            <div className="cell-sub" style={{ marginTop: 6 }}>
              Click the image to enlarge
            </div>
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No preview for ${licenseName}. Open it in a new tab to check.`}
          />
        )}
      </Section>
    );

    const allowedProviders = detailClient.allowedProviders || [];
    const accountTab = (
      <>
        <Section title="Pricing and access">
          <dl className="kv">
            <dt>Pricing tier</dt>
            <dd>
              {detailHasAccount ? (
                tierLabel(detailClient.tier)
              ) : (
                <span className="muted">Set when the registration is approved</span>
              )}
            </dd>
            <dt>Voucher providers</dt>
            <dd>
              {allowedProviders.length === 0 ? (
                <span className="muted">All providers (no restriction)</span>
              ) : (
                <Space wrap size={[4, 4]}>
                  {allowedProviders.map((p) => (
                    <Tag key={p}>{formatVoucherProviderLabel(p)}</Tag>
                  ))}
                </Space>
              )}
            </dd>
          </dl>
        </Section>

        <Section title="Balance and credit">
          {detailHasAccount ? (
            <dl className="kv">
              <dt>Top-up balance</dt>
              <dd className="cell-money">
                {formatMoney(detailFunds.topUpBalance, detailCurrency)}
              </dd>
              <dt>Credit limit</dt>
              <dd className="cell-money">
                {formatMoney(detailFunds.creditLimit, detailCurrency)}
              </dd>
              <dt>Credit used</dt>
              <dd className="cell-money">
                {formatMoney(detailFunds.creditUsed, detailCurrency)}
              </dd>
              <dt>Credit utilization</dt>
              <dd>
                {detailFunds.creditLimit > 0 ? (
                  <Progress
                    percent={Math.min(creditUtilization, 100)}
                    format={() => `${creditUtilization}%`}
                    strokeColor={utilizationColor}
                    size="small"
                    style={{ maxWidth: 260, margin: 0 }}
                  />
                ) : (
                  "0%"
                )}
              </dd>
              <dt>Available credit</dt>
              <dd className="cell-money">
                {formatMoney(detailFunds.availableCredit, detailCurrency)}
              </dd>
              <dt>Total available</dt>
              <dd className="cell-money">
                {formatMoney(detailFunds.totalAvailable, detailCurrency)}
              </dd>
            </dl>
          ) : (
            <Text type="secondary">
              Balances and credit apply to approved and suspended clients.
            </Text>
          )}
        </Section>

        {detail.transactionStats ? (
          <Section title="Transaction totals">
            <dl className="kv">
              <dt>Top-up</dt>
              <dd className="cell-money">
                {formatMoney(
                  detail.transactionStats.topup?.amount || 0,
                  detailCurrency
                )}
              </dd>
              <dt>Repayment</dt>
              <dd className="cell-money">
                {formatMoney(
                  detail.transactionStats.repay?.amount || 0,
                  detailCurrency
                )}
              </dd>
              <dt>Consumption</dt>
              <dd className="cell-money">
                {formatMoney(
                  detail.transactionStats.consume?.amount || 0,
                  detailCurrency
                )}
              </dd>
            </dl>
          </Section>
        ) : null}
      </>
    );

    const ordersTab = (
      <>
        <Section
          title="Voucher redemption"
          extra={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={redeemMetricsLoading}
              onClick={() => fetchClientRedeemMetrics(detailClient._id)}
            >
              Refresh
            </Button>
          }
        >
          <StatGrid min={150}>
            <StatCard
              label="Total"
              value={redeemMetrics?.summary?.totalRedeemQuantity || 0}
              loading={redeemMetricsLoading && !redeemMetrics}
            />
            <StatCard
              label="Redeemed"
              value={redeemMetrics?.summary?.completedRedeemQuantity || 0}
              loading={redeemMetricsLoading && !redeemMetrics}
            />
            <StatCard
              label="Unredeemed"
              value={redeemMetrics?.summary?.pendingRedeemQuantity || 0}
              loading={redeemMetricsLoading && !redeemMetrics}
            />
          </StatGrid>
          <Table
            {...nestedTableProps}
            rowKey={(record) => record.voucherId}
            loading={redeemMetricsLoading}
            dataSource={redeemMetrics?.vouchers || []}
            columns={redeemColumns}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No vouchers redeemed yet"
                />
              ),
            }}
          />
        </Section>

        <Section title="Recent orders" description="Up to the 10 most recent">
          <Table
            {...nestedTableProps}
            rowKey="_id"
            dataSource={detail.recentOrders || []}
            columns={recentOrderColumns}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No orders yet"
                />
              ),
            }}
          />
        </Section>

        <Section
          title="Recent top-up requests"
          description="Up to the 10 most recent"
        >
          <Table
            {...nestedTableProps}
            rowKey="_id"
            dataSource={detail.recentTopUpRequests || []}
            columns={recentTopUpColumns}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No top-up requests yet"
                />
              ),
            }}
          />
        </Section>
      </>
    );

    const usersTab = (
      <Section title="Associated users" description="Users linked to this client account">
        <Table
          {...nestedTableProps}
          rowKey="_id"
          dataSource={detailUsers}
          columns={userColumns}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No other users linked to this client"
              />
            ),
          }}
        />
      </Section>
    );

    return (
      <Tabs
        activeKey={detailTab}
        onChange={setDetailTab}
        items={[
          { key: "overview", label: "Overview", children: overviewTab },
          { key: "documents", label: "Documents", children: documentsTab },
          { key: "account", label: "Account & credit", children: accountTab },
          { key: "orders", label: "Orders", children: ordersTab },
          {
            key: "users",
            label: detailUsers.length ? `Users (${detailUsers.length})` : "Users",
            children: usersTab,
          },
        ]}
      />
    );
  };

  let drawerFooter = null;
  if (detailClient && detailStatus === "pending") {
    drawerFooter = (
      <div className="sheet-footer">
        <Text type="secondary">
          The client is emailed the outcome either way.
        </Text>
        <div className="sheet-footer__actions">
          <Button
            danger
            icon={<CloseOutlined />}
            onClick={() => setRejectModalVisible(true)}
          >
            Reject
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => setApproveModalVisible(true)}
          >
            Approve
          </Button>
        </div>
      </div>
    );
  } else if (detailClient && detailStatus === "approved") {
    drawerFooter = (
      <div className="sheet-footer">
        <div className="sheet-footer__actions">
          <Button
            danger
            icon={<StopOutlined />}
            onClick={() => setSuspendModalVisible(true)}
          >
            Suspend
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setEditModalVisible(true)}
          >
            Edit settings
          </Button>
        </div>
      </div>
    );
  } else if (detailClient && detailStatus === "suspended") {
    drawerFooter = (
      <div className="sheet-footer">
        <div className="sheet-footer__actions">
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => setReactivateModalVisible(true)}
          >
            Reactivate
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle="Review new registrations, then manage each client's pricing tier, credit limit and provider access."
        extra={
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => fetchClients(pagination.current, pagination.pageSize)}
          >
            Refresh
          </Button>
        }
      />

      <StatGrid>
        {STATUS_TABS.map((tab) => (
          <StatCard
            key={tab.key}
            label={tab.label}
            value={statistics?.[tab.statKey] ?? "—"}
            icon={tab.icon}
            tone={tab.tone}
            highlight={tab.key === "pending" && (statistics?.pending || 0) > 0}
            active={activeStatusFilter === tab.key}
            onClick={() => handleStatusPanelClick(tab.key)}
          />
        ))}
      </StatGrid>

      <FilterBar onReset={resetFilters} resetDisabled={!filtersDirty}>
        <FilterBar.Item grow>
          <Input
            placeholder="Search company, contact or email"
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            allowClear
          />
        </FilterBar.Item>
        {showTierFilter ? (
          <FilterBar.Item width={180}>
            <Select
              placeholder="All tiers"
              value={filters.tier || undefined}
              onChange={(value) => applyFilter("tier", value)}
              allowClear
              {...responsiveFilterSelectProps}
            >
              {pricingTierOptions
                .filter((option) => !option.isOthers)
                .map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label.replace(/ \(.*\)$/, "")}
                  </Option>
                ))}
            </Select>
          </FilterBar.Item>
        ) : null}
        <FilterBar.Item width={280}>
          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => applyFilter("dateRange", dates)}
            placeholder={["Registered from", "Registered to"]}
          />
        </FilterBar.Item>
      </FilterBar>

      <TableCard title={activeTab.label} count={pagination.total}>
        <Table
          {...tableDefaults}
          columns={columns}
          dataSource={clients}
          rowKey="_id"
          loading={loading}
          rowClassName={(record) =>
            record.status === "pending" && activeStatusFilter !== "pending"
              ? "row-clickable row-attention"
              : "row-clickable"
          }
          onRow={(record) => ({ onClick: () => handleViewDetails(record._id) })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  activeStatusFilter === "pending"
                    ? "No registrations waiting for review"
                    : filtersDirty
                    ? "No clients match these filters"
                    : "No clients yet"
                }
              />
            ),
          }}
          pagination={{
            ...paginationDefaults,
            ...pagination,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} clients`,
          }}
          onChange={handleTableChange}
        />
      </TableCard>

      {/* Client details drawer */}
      <Drawer
        title={
          detailClient ? (
            <Space size={10} wrap>
              <span>{detailClient.companyName || detailClient.email}</span>
              {renderClientStatus(detailClient.status)}
            </Space>
          ) : (
            "Client"
          )
        }
        open={detailModalVisible}
        onClose={closeDrawer}
        afterOpenChange={handleDrawerAfterOpenChange}
        width={DRAWER_WIDTH.xl}
        loading={detailLoading}
        destroyOnHidden
        footer={drawerFooter}
      >
        {detailClient ? (
          renderDetailTabs()
        ) : detailLoading ? null : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No client details available"
          />
        )}
      </Drawer>

      {/* Approve registration */}
      <Modal
        title="Approve client registration"
        open={approveModalVisible}
        onCancel={() => {
          setApproveModalVisible(false);
        }}
        afterClose={() => {
          // Reset the form only after the close animation finishes. Doing it
          // synchronously in onCancel/handleApproveClient causes the
          // InputNumber to briefly show the initial value (5000) before the
          // modal disappears, which makes users think their edit (e.g. 10000)
          // was reverted.
          approveForm.resetFields();
        }}
        width={MODAL_WIDTH.md}
        zIndex={OVER_DRAWER_Z_INDEX}
        maskClosable={false}
        footer={[
          <Button
            key="cancel"
            onClick={() => setApproveModalVisible(false)}
            disabled={actionSubmitting}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<CheckOutlined />}
            loading={actionSubmitting}
            onClick={() => approveForm.submit()}
          >
            Approve client
          </Button>,
        ]}
      >
        {selectedClient && (
          <Form
            form={approveForm}
            layout="vertical"
            onFinish={handleApproveClient}
            initialValues={{
              tier: "basic",
              creditLimit: 5000,
            }}
          >
            <p style={{ marginTop: 0 }}>
              <strong>{targetName}</strong> will be able to sign in and order
              vouchers. The client is emailed the pricing tier and credit limit
              you set here.
            </p>
            <dl className="kv" style={{ marginBottom: 20 }}>
              <dt>Email</dt>
              <dd>{target?.email || "-"}</dd>
              <dt>Country</dt>
              <dd>{getCountryDisplayName(target?.country) || "-"}</dd>
              <dt>Settlement currency</dt>
              <dd>{target?.currency || "-"}</dd>
            </dl>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="tier"
                  label="Pricing tier"
                  rules={[
                    { required: true, message: "Please select a pricing tier" },
                  ]}
                >
                  <Select placeholder="Select pricing tier">
                    {pricingTierOptions
                      .filter((option) => option.value !== "others") // Remove others option
                      .map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="creditLimit"
                  label={`Credit limit (${
                    selectedClient?.currency ||
                    selectedClient?.client?.currency ||
                    "USD"
                  })`}
                  rules={[
                    { required: true, message: "Please enter credit limit" },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    formatter={(value) => {
                      const currency =
                        selectedClient?.currency ||
                        selectedClient?.client?.currency ||
                        "USD";
                      const code = currency?.toUpperCase() || "USD";
                      const symbol = getCurrencySymbol(currency);
                      return `${code} ${symbol}${value}`.replace(
                        /\B(?=(\d{3})+(?!\d))/g,
                        ","
                      );
                    }}
                    parser={(value) => value.replace(/[^\d.]/g, "")}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="notes"
              label="Note (optional)"
              style={{ marginBottom: 0 }}
            >
              <TextArea
                rows={3}
                placeholder="Saved on the client record"
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Reject registration */}
      <Modal
        title="Reject client registration"
        open={rejectModalVisible}
        onCancel={closeRejectModal}
        width={MODAL_WIDTH.sm}
        zIndex={OVER_DRAWER_Z_INDEX}
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={closeRejectModal} disabled={actionSubmitting}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={actionSubmitting}
            onClick={() => rejectForm.submit()}
          >
            Reject registration
          </Button>,
        ]}
      >
        {selectedClient && (
          <Form
            form={rejectForm}
            layout="vertical"
            onFinish={handleRejectClient}
          >
            <p style={{ marginTop: 0 }}>
              <strong>{targetName}</strong>'s registration will be rejected and
              the client is emailed the reason below.
            </p>
            <Form.Item
              name="reason"
              label="Reason for rejection"
              rules={[
                {
                  required: true,
                  message: "Please provide a rejection reason",
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <TextArea
                rows={4}
                autoFocus
                placeholder="e.g. The business license is unreadable. Please upload a clearer copy."
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Suspend client */}
      <Modal
        title="Suspend client"
        open={suspendModalVisible}
        onCancel={closeSuspendModal}
        width={MODAL_WIDTH.sm}
        zIndex={OVER_DRAWER_Z_INDEX}
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={closeSuspendModal} disabled={actionSubmitting}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={actionSubmitting}
            onClick={() => suspendForm.submit()}
          >
            Suspend client
          </Button>,
        ]}
      >
        {selectedClient && (
          <Form
            form={suspendForm}
            layout="vertical"
            onFinish={handleSuspendClient}
          >
            <p style={{ marginTop: 0 }}>
              <strong>{targetName}</strong> loses access to their account until
              you reactivate it. The client is emailed the reason below.
            </p>
            <Form.Item
              name="reason"
              label="Reason for suspension"
              rules={[
                {
                  required: true,
                  message: "Please provide a suspension reason",
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <TextArea
                rows={4}
                autoFocus
                placeholder="e.g. Repayment overdue for more than 30 days"
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Reactivate client */}
      <Modal
        title="Reactivate client"
        open={reactivateModalVisible}
        onCancel={closeReactivateModal}
        width={MODAL_WIDTH.sm}
        zIndex={OVER_DRAWER_Z_INDEX}
        footer={[
          <Button
            key="cancel"
            onClick={closeReactivateModal}
            disabled={actionSubmitting}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={actionSubmitting}
            onClick={handleReactivateClient}
          >
            Reactivate client
          </Button>,
        ]}
      >
        {selectedClient && (
          <p style={{ margin: 0 }}>
            <strong>{targetName}</strong> gets access to their account again,
            with their current pricing tier and credit limit. The client is
            emailed that the account is active.
          </p>
        )}
      </Modal>

      {/* Credit Management Modal (not opened from this page at the moment) */}
      <Modal
        title={`Edit credit limit · ${targetName}`}
        open={creditModalVisible}
        onCancel={() => {
          setCreditModalVisible(false);
          setSelectedClient(null);
        }}
        width={MODAL_WIDTH.sm}
        zIndex={OVER_DRAWER_Z_INDEX}
        footer={[
          <Button key="cancel" onClick={() => setCreditModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={() => creditForm.submit()}>
            Save credit limit
          </Button>,
        ]}
        afterOpenChange={(open) => {
          if (open && selectedClient) {
            // Auto fetch rate when modal opens
            const currency =
              selectedClient.client?.currency || selectedClient.currency;
            if (currency && currency !== "USD") {
              fetchExchangeRate(currency, "USD");
            }
          }
        }}
      >
        {selectedClient && (
          <Form
            form={creditForm}
            key={selectedClient.client?._id || selectedClient._id}
            layout="vertical"
            initialValues={{
              creditLimit:
                selectedClient.client?.creditLimit ||
                selectedClient.creditLimit ||
                0,
            }}
            onFinish={async (values) => {
              const success = await handleUpdateCreditSettings(
                selectedClient.client?._id || selectedClient._id,
                values
              );
              if (success) {
                setCreditModalVisible(false);
              }
            }}
          >
            <Form.Item
              label={(() => {
                const currency =
                  selectedClient.client?.currency || selectedClient.currency;
                const code = currency?.toUpperCase() || "USD";
                return `Credit limit (${code})`;
              })()}
              name="creditLimit"
              rules={[
                { required: true, message: "Please enter credit limit" },
                {
                  type: "number",
                  min: 0,
                  message: "Credit limit must be non-negative",
                },
              ]}
              extra={(() => {
                const currency =
                  selectedClient.client?.currency || selectedClient.currency;
                if (currency && currency !== "USD") {
                  const currentValue =
                    creditForm.getFieldValue("creditLimit") || 0;
                  return (
                    <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                      {formatUSDEquivalent(currentValue, currency)}
                      {loadingRates && (
                        <span style={{ marginLeft: 8, color: "var(--c-primary)" }}>
                          Updating rates...
                        </span>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Enter credit limit"
                precision={2}
                min={0}
                step={1000}
                formatter={(value) => {
                  const currency =
                    selectedClient.client?.currency || selectedClient.currency;
                  const code = currency?.toUpperCase() || "USD";
                  const symbol = getCurrencySymbol(currency);
                  return `${code} ${symbol}${value}`.replace(
                    /\B(?=(\d{3})+(?!\d))/g,
                    ","
                  );
                }}
                parser={(value) => value.replace(/[^\d.]/g, "")}
                onChange={(value) => {
                  // Fetch latest rate when value changes
                  const currency =
                    selectedClient.client?.currency || selectedClient.currency;
                  if (currency && currency !== "USD" && value) {
                    fetchExchangeRate(currency, "USD");
                  }
                }}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Top-up Management Modal (not opened from this page at the moment) */}
      <Modal
        title={`Manual top-up · ${targetName}`}
        open={topUpModalVisible}
        onCancel={() => {
          setTopUpModalVisible(false);
          setSelectedClient(null);
          topUpForm.resetFields();
        }}
        width={MODAL_WIDTH.md}
        zIndex={OVER_DRAWER_Z_INDEX}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setTopUpModalVisible(false);
              setSelectedClient(null);
              topUpForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => topUpForm.submit()}
          >
            Add top-up
          </Button>,
        ]}
      >
        {selectedClient && (
          <>
            <Section title="Current balance">
              <dl className="kv">
                <dt>Top-up balance</dt>
                <dd className="cell-money">
                  {formatMoney(
                    selectedClient.client?.topUpBalance ||
                      selectedClient.topUpBalance ||
                      0,
                    selectedClient.client?.currency || selectedClient.currency
                  )}
                </dd>
                <dt>Credit available</dt>
                <dd className="cell-money">
                  {formatMoney(
                    Math.max(
                      0,
                      (selectedClient.client?.creditLimit ||
                        selectedClient.creditLimit ||
                        0) - (selectedClient.client?.creditUsed || 0)
                    ),
                    selectedClient.client?.currency || selectedClient.currency
                  )}
                </dd>
                <dt>Total available</dt>
                <dd className="cell-money">
                  {formatMoney(
                    (selectedClient.client?.topUpBalance ||
                      selectedClient.topUpBalance ||
                      0) +
                      Math.max(
                        0,
                        (selectedClient.client?.creditLimit ||
                          selectedClient.creditLimit ||
                          0) - (selectedClient.client?.creditUsed || 0)
                      ),
                    selectedClient.client?.currency || selectedClient.currency
                  )}
                </dd>
              </dl>
            </Section>

            <Section title="Top-up">
              <Form
                form={topUpForm}
                layout="vertical"
                onFinish={handleManualTopUp}
              >
                <Form.Item
                  name="amount"
                  label="Top-up amount"
                  rules={[
                    { required: true, message: "Please enter amount" },
                    {
                      type: "number",
                      min: 0.01,
                      message: "Amount must be greater than 0",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Enter amount"
                    precision={2}
                    min={0.01}
                    step={100}
                    formatter={(value) => {
                      const currency =
                        selectedClient.client?.currency ||
                        selectedClient.currency;
                      const code = currency?.toUpperCase() || "USD";
                      const symbol = getCurrencySymbol(currency);
                      return `${code} ${symbol}${value}`.replace(
                        /\B(?=(\d{3})+(?!\d))/g,
                        ","
                      );
                    }}
                    parser={(value) => value.replace(/[^\d.]/g, "")}
                  />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Description (optional)"
                  style={{ marginBottom: 0 }}
                >
                  <TextArea
                    rows={3}
                    placeholder="Shown on the client's transaction history"
                  />
                </Form.Item>
              </Form>
            </Section>
          </>
        )}
      </Modal>

      {/* Top-up Requests Modal (not opened from this page at the moment) */}
      <Modal
        title={`Top-up requests · ${targetName}`}
        open={topUpRequestsModalVisible}
        onCancel={() => {
          setTopUpRequestsModalVisible(false);
          setSelectedClient(null);
          setTopUpRequests([]);
        }}
        width={MODAL_WIDTH.lg}
        zIndex={OVER_DRAWER_Z_INDEX}
        footer={null}
      >
        <List
          dataSource={topUpRequests}
          renderItem={(request) => (
            <List.Item
              actions={[
                request.status === "pending" && (
                  <Button
                    type="primary"
                    size="small"
                    loading={
                      topUpActionLoading &&
                      topUpActionRequestId === request._id
                    }
                    disabled={topUpActionLoading}
                    onClick={() => {
                      Modal.confirm({
                        title: "Approve top-up request",
                        content:
                          "Approving will credit the client's account immediately. This action cannot be undone. Continue?",
                        okText: "Approve and credit wallet",
                        cancelText: "Cancel",
                        onOk: () =>
                          handleTopUpRequestApproval(request._id, "approve"),
                      });
                    }}
                  >
                    Approve
                  </Button>
                ),
                request.status === "pending" && (
                  <Button
                    danger
                    size="small"
                    loading={
                      topUpActionLoading &&
                      topUpActionRequestId === request._id
                    }
                    disabled={topUpActionLoading}
                    onClick={() => {
                      Modal.confirm({
                        title: "Reject top-up request",
                        content: (
                          <div>
                            <p>
                              Are you sure you want to reject this top-up
                              request?
                            </p>
                            <Input.TextArea
                              placeholder="Enter rejection reason..."
                              id="rejection-reason"
                              rows={3}
                            />
                          </div>
                        ),
                        onOk: async () => {
                          const reason =
                            document.getElementById("rejection-reason")?.value;
                          if (!reason?.trim()) {
                            message.error("Please provide a reason");
                            return false;
                          }
                          return await handleTopUpRequestApproval(
                            request._id,
                            "reject",
                            reason
                          );
                        },
                      });
                    }}
                  >
                    Reject
                  </Button>
                ),
              ].filter(Boolean)}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <span className="cell-mono">
                      {request.requestNumber || request._id}
                    </span>
                    {renderReviewStatus(request.status)}
                  </Space>
                }
                description={
                  <div>
                    <dl className="kv">
                      <dt>Amount</dt>
                      <dd className="cell-money">
                        {formatMoney(request.amount, request.currency)}
                      </dd>
                      <dt>Payment method</dt>
                      <dd>{humanize(request.paymentMethod)}</dd>
                      <dt>Reference</dt>
                      <dd>{request.paymentReference || "-"}</dd>
                      <dt>Submitted</dt>
                      <dd>{formatDateTime(request.createdAt)}</dd>
                      {request.reviewedAt && (
                        <>
                          <dt>Reviewed</dt>
                          <dd>{formatDateTime(request.reviewedAt)}</dd>
                        </>
                      )}
                    </dl>
                    {request.paymentProof && (
                      <div style={{ marginTop: 12 }}>
                        {request.paymentProof.mimeType?.startsWith("image/") ? (
                          <Image
                            src={getUploadURL(
                              "topup-proofs",
                              request.paymentProof.filename
                            )}
                            alt="Payment proof"
                            style={{ maxWidth: "100%", maxHeight: 300 }}
                          />
                        ) : (
                          <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() =>
                              window.open(
                                getUploadURL(
                                  "topup-proofs",
                                  request.paymentProof.filename
                                ),
                                "_blank"
                              )
                            }
                          >
                            Download payment proof
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* Custom Pricing Tier Modal (not opened from this page at the moment) */}
      <Modal
        title="Create custom pricing tier"
        open={customTierModalVisible}
        onCancel={() => setCustomTierModalVisible(false)}
        footer={null}
        width={MODAL_WIDTH.md}
        zIndex={OVER_DRAWER_Z_INDEX}
      >
        <Form
          layout="vertical"
          onFinish={async (values) => {
            try {
              // Create custom tier
              const tierResponse = await axios.post("/admin/pricing-tiers", {
                name: values.displayName.toLowerCase().replace(/\s+/g, "-"),
                displayName: values.displayName,
                multiplier: values.multiplier,
                description: values.description || "",
              });

              const newTierName = tierResponse.data.tier.name;
              message.success("Custom pricing tier created successfully");

              // Refresh pricing tier options first
              await fetchPricingTierOptions();

              // Small delay to ensure state update complete
              setTimeout(() => {
                // Set the new tier in the approve form
                approveForm.setFieldsValue({ tier: newTierName });
                setCustomTierModalVisible(false);
              }, 100);
            } catch (error) {
              console.error("Error creating custom tier:", error);
              showApiError(error, "Failed to create custom tier");
            }
          }}
        >
          <p style={{ marginTop: 0 }}>
            The new tier becomes available for every future client approval.
          </p>

          <Form.Item
            name="displayName"
            label="Tier name"
            rules={[
              { required: true, message: "Please enter tier name" },
              { max: 50, message: "Name cannot exceed 50 characters" },
            ]}
          >
            <Input placeholder="e.g., Enterprise VIP, Wholesale Partner" />
          </Form.Item>

          <Form.Item
            name="multiplier"
            label="Price multiplier"
            rules={[
              { required: true, message: "Please enter price multiplier" },
              {
                type: "number",
                min: 0.1,
                max: 2.0,
                message: "Multiplier must be between 0.1 and 2.0",
              },
            ]}
            extra="0.8 = 20% discount, 1.0 = no change, 1.2 = 20% markup"
          >
            <InputNumber
              style={{ width: "100%" }}
              step={0.01}
              precision={2}
              placeholder="e.g., 0.85 for 15% discount"
              formatter={(value) => `${value}x`}
              parser={(value) => value.replace("x", "")}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (optional)"
            rules={[
              { max: 200, message: "Description cannot exceed 200 characters" },
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Describe this tier or its intended use..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={() => setCustomTierModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create tier
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit client settings */}
      <Modal
        title="Edit client settings"
        open={editModalVisible}
        onCancel={closeEditModal}
        width={MODAL_WIDTH.lg}
        zIndex={OVER_DRAWER_Z_INDEX}
        {...formModalProps}
        footer={[
          <Button key="cancel" onClick={closeEditModal} disabled={actionSubmitting}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={actionSubmitting}
            onClick={() => editForm.submit()}
          >
            Save changes
          </Button>,
        ]}
      >
        {selectedClient && (
          <Form form={editForm} layout="vertical" onFinish={handleEditClient}>
            <p className="muted" style={{ marginTop: 0 }}>
              {[target?.companyName, target?.email].filter(Boolean).join(" · ")}
            </p>

            <Section title="Company and contact">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="companyName"
                    label="Company name"
                    rules={[{ required: true, message: "Company name is required" }]}
                  >
                    <Input placeholder="Company name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="name" label="Contact person">
                    <Input placeholder="Contact person name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="firstName"
                    label="First name"
                    rules={[{ required: true, message: "First name is required" }]}
                  >
                    <Input placeholder="First name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="lastName"
                    label="Last name"
                    rules={[{ required: true, message: "Last name is required" }]}
                  >
                    <Input placeholder="Last name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[{ required: true, message: "Phone is required for purchases" }]}
                  >
                    <Input placeholder="Phone number" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="country" label="Country">
                    <Input placeholder="Country" />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="address" label="Address" style={{ marginBottom: 0 }}>
                    <Input placeholder="Address" />
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            <Section title="Account settings">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tier"
                    label="Pricing tier"
                    rules={[{ required: true, message: "Please select a tier" }]}
                  >
                    <Select placeholder="Select pricing tier">
                      {pricingTierOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="creditLimit"
                    label={`Credit limit (${
                      selectedClient?.currency ||
                      selectedClient?.client?.currency ||
                      "USD"
                    })`}
                    rules={[
                      { required: true, message: "Please enter credit limit" },
                      {
                        type: "number",
                        min: 0,
                        message: "Credit limit must be positive",
                      },
                    ]}
                    extra={`Client's settlement currency: ${
                      selectedClient?.currency ||
                      selectedClient?.client?.currency ||
                      "USD"
                    }`}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder={`Enter credit limit in ${
                        selectedClient?.currency ||
                        selectedClient?.client?.currency ||
                        "USD"
                      }`}
                      formatter={(value) => {
                        const currency =
                          selectedClient?.currency ||
                          selectedClient?.client?.currency ||
                          "USD";
                        const symbol = getCurrencySymbol(currency);
                        return `${symbol} ${value}`.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ","
                        );
                      }}
                      parser={(value) => value.replace(/[^\d.]/g, "")}
                      min={0}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="allowedProviders"
                    label="Allowed voucher providers"
                    extra="Restrict which integrated voucher providers and platform internal vouchers this client can see and purchase. Leave empty to allow all providers."
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Leave empty to allow all providers"
                      options={providerOptions.map((p) => ({
                        value: p,
                        label: formatVoucherProviderLabel(p),
                      }))}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="notes"
                    label="Notes"
                    rules={[
                      { max: 500, message: "Notes cannot exceed 500 characters" },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <TextArea
                      rows={3}
                      placeholder="Add any notes about this client..."
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Section>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default ClientManagement;
